/**
 * Hook for bulk-updating the assigned-to field on multiple PA orders via Postgres.
 * @module useAssignedToUpdate
 */

import { useCallback, useEffect } from "react";

import {
  bulkOrderUpdateForAssignment,
  BulkOrderUpdateForAssignmentResponse,
} from "../../../../api/postgreSQL/postCalls/bulkOrderUpdateForAssignment";
import { useAbortableFetch } from "../../../../hooks/useAbortableFetch";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";

/** Payload forwarded by the hook's `runUpdate` function. */
interface AssignedToUpdatePayload<T> {
  /** List of order IDs to reassign. */
  orderIds: string[];
  /** Assignment data to apply to every order in the batch. */
  data: T;
}

/**
 * Provides an abortable bulk-update function for reassigning PA orders in Postgres.
 *
 * Does not auto-fetch on mount. Exposes a `runUpdate` function that callers
 * invoke with the order IDs and assignment payload.
 * In-flight requests are automatically cancelled on unmount.
 *
 * By default, POST /medical/frontend-query/update/bulk/order updates only the
 * supplied order IDs. Opting in uses POST
 * /medical/frontend-query/update/bulk/assignee, where the backend also includes
 * direct siblings in the same organization with the source's current assignee
 * and a yet-to-start or in-progress status. The dedicated endpoint accepts only
 * `assigned_to_id` and `assigned_to_name` in the assignment payload.
 *
 * @typeParam T - Shape of the assignment data payload.
 * @param useAssignmentEndpoint - Use the dedicated assignment endpoint, which
 * includes eligible siblings. Defaults to false for selected-order updates.
 * @returns runUpdate - Triggers the bulk update with order IDs and data.
 * @returns abort     - Manually cancel any in-flight update request.
 * @returns data      - The update response (null until a request completes).
 * @returns loading   - Whether an update request is in progress.
 * @returns error     - Error object if the update failed.
 *
 * @example
 * ```tsx
 * type AssigneeData = {
 *   assigned_to_id: string | null;
 *   assigned_to_name: string | null;
 * };
 *
 * // Update only the supplied orders (the default).
 * const selectedOrders = useAssignedToUpdate<AssigneeData>();
 * // Let the backend include eligible direct siblings.
 * const withSiblings = useAssignedToUpdate<AssigneeData>(true);
 * await withSiblings.runUpdate([orderId], {
 *   assigned_to_id: providerId,
 *   assigned_to_name: providerName,
 * });
 * ```
 */
export const useAssignedToUpdate = <T,>(useAssignmentEndpoint = false) => {
  const fetchFn = useCallback(
    async (signal: AbortSignal, payload: AssignedToUpdatePayload<T>) => {
      return bulkOrderUpdateForAssignment<T>(
        {
          order_ids: payload.orderIds,
          org_id: getOrgIdForFetchExternalWorklist(),
          data: payload.data,
          useAssignmentEndpoint,
        },
        signal,
      );
    },
    [useAssignmentEndpoint],
  );

  const { runFetch, abort, data, loading, error } =
    useAbortableFetch<BulkOrderUpdateForAssignmentResponse>(fetchFn);

  useEffect(() => {
    return () => abort();
  }, [abort]);

  const runUpdate = useCallback(
    async (orderIds: string[], updateData: T) =>
      runFetch({ orderIds, data: updateData }),
    [runFetch],
  );

  return { runUpdate, abort, data, loading, error };
};
